import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppAuthService, LowScoreFallbackRequiredError } from './app-auth.service';
import { ActivatedRoute, Params, Router, RouterModule } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { GoogleSigninButtonComponent } from './google-signin-button.component';
import { GoogleIdTokenService, GoogleSignInEvent, RELAY_PARAMS } from './google-id-token.service';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { LowercaseDirective } from '../directives/lowercase.directive';
import { MatIconModule } from '@angular/material/icon';
import { AutotrimDirective } from '../directives/autotrim.directive';
import { FormActionsComponent } from '../form-actions.component';
import { TranslateModule } from '@ngx-translate/core';
import { TranslationService } from '../service/translation-service.service';
import { RecaptchaV2FallbackComponent } from './recaptcha-v2-fallback.component';
import { PasskeyService } from './passkey.service';
import { environment } from '../../../environments/environment';

/**
 * Which post-login `returnUrl` values may be reached with a hard `window.location` redirect
 * instead of `router.navigate`.
 *
 * `ownOrigin` is in the list because the knowledge-base single-sign-on bootstrap page
 * (`/sso/kb`) is server-rendered, so the Angular router cannot reach it at all — and because
 * its `next` parameter carries a query string, which `router.navigate([url])` would encode
 * into a path segment rather than treat as a query. Passing the origin in rather than reading
 * `window` keeps this testable, and listing an *origin* rather than a hostname is what makes
 * it correct in every environment: the app answers on `app.`, `dev.`, `test.`, `demo.` and
 * every `dev-<branch>` host, so a literal host list would be wrong in most of them and would
 * silently drop the user on the dashboard instead of the document they asked for.
 *
 * Everything else stays a relative route, which is what keeps this from being an open
 * redirect: an absolute URL that is not one of these three is refused, not followed.
 *
 * A same-origin `/wiki` path is also followed with a hard navigation, even though it is
 * relative: the frontend Wiki route handles the redirect to the configured Wiki host. This is
 * not an open redirect because the eventual host is fixed in the frontend environment config.
 */
export function isHardRedirectAllowed(url: string, ownOrigin: string): boolean {
  const allowedOrigins = [ownOrigin, environment.wikiBaseUrl, 'https://forms.supplycanvas.com'];
  return (
    allowedOrigins.some((origin) => url === origin || url.startsWith(origin + '/')) ||
    url === '/wiki' ||
    url.startsWith('/wiki/')
  );
}

/**
 * A ceremony the user walked away from (picker dismissed, or aborted by a newer ceremony) — not a
 * failure worth surfacing. `@simplewebauthn/browser` reports the abort as a `WebAuthnError` with
 * `code = ERROR_CEREMONY_ABORTED`; a dismissed picker surfaces as a `NotAllowedError`, either
 * directly or wrapped as the `cause` of a `WebAuthnError`.
 */
function isCancelledCeremony(error: any): boolean {
  return (
    error?.code === 'ERROR_CEREMONY_ABORTED' ||
    error?.name === 'NotAllowedError' ||
    error?.cause?.name === 'NotAllowedError'
  );
}

function isAccountDisabledError(err: unknown): boolean {
  const e = err as { status?: number; error?: { error_type?: string } };
  return e?.status === 403 && e?.error?.error_type === 'account_disabled';
}

/** Translation key for a failed password login; the server sends email_not_verified only after the password checked out (app#355). */
export function loginErrorKey(err: unknown): string {
  const e = err as { status?: number; error?: { error_type?: string } };
  if (e?.status === 401) {
    return 'Invalid_email_or_password_tr';
  }
  if (e?.status === 403 && e?.error?.error_type === 'email_not_verified') {
    return 'login_email_not_verified_tr';
  }
  return 'login_error_occurred_tr';
}

@Component({
  selector: 'app-login',
  templateUrl: 'login.component.html',
  styleUrls: ['login-reset-register.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    LowercaseDirective,
    GoogleSigninButtonComponent,
    RouterModule,
    AutotrimDirective,
    FormActionsComponent,
    TranslateModule,
    RecaptchaV2FallbackComponent,
  ],
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AppAuthService);
  private readonly googleSignIn = inject(GoogleIdTokenService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslationService);
  private readonly passkeyService = inject(PasskeyService);
  noRegister = true;
  loginForm = new FormBuilder().nonNullable.group({
    email: ['', Validators.required],
    password: ['', Validators.required],
  });
  loginError?: string;
  showV2Fallback = false;
  showPassword = false;
  recaptchaNeedsAttention = false;
  passkeySupported = false;
  passkeyBusy = false;
  private pendingEmail: string | null = null;
  private pendingPassword: string | null = null;

  get returnUrl(): string {
    return this.route.snapshot?.queryParams?.['returnUrl'] || '/';
  }

  get isInviteReturnFlow(): boolean {
    return this.returnUrl.startsWith('/invite/');
  }

  get authQueryParams(): Params {
    const params = { ...(this.route.snapshot?.queryParams ?? {}) } as Params;
    // The relay return leg cleans the browser URL with history.replaceState, which the router
    // snapshot never sees — never forward a used relay code/state to the register or reset page.
    RELAY_PARAMS.forEach((p) => delete params[p]);
    const email = this.loginForm.controls.email.value;
    if (email) {
      params['email'] = email;
    }
    return params;
  }

  async login() {
    this.passkeyService.cancelCeremony();
    const { email, password } = this.loginForm.value;
    this.loginForm.markAsPristine();
    this.loginError = undefined;
    try {
      await this.authService.login(email!, password!);
      await this.handleAfterLogin();
    } catch (error: any) {
      if (error instanceof LowScoreFallbackRequiredError) {
        this.pendingEmail = email!;
        this.pendingPassword = password!;
        this.showV2Fallback = true;
        this.recaptchaNeedsAttention = true;
        return;
      }
      if (isAccountDisabledError(error)) {
        await this.router.navigate(['/waiting']);
        return;
      }
      this.loginError = await this.translate.loadFromBackend(loginErrorKey(error));
    }
  }

  async onV2Solved(token: string) {
    this.passkeyService.cancelCeremony();
    this.loginError = undefined;
    try {
      await this.authService.login(this.pendingEmail!, this.pendingPassword!, token);
      this.showV2Fallback = false;
      this.recaptchaNeedsAttention = false;
      this.pendingEmail = null;
      this.pendingPassword = null;
      await this.handleAfterLogin();
    } catch (error: any) {
      if (error instanceof LowScoreFallbackRequiredError) {
        // server rejected v2 token too — leave widget visible so user can retry
        return;
      }
      this.showV2Fallback = false;
      this.recaptchaNeedsAttention = false;
      if (isAccountDisabledError(error)) {
        await this.router.navigate(['/waiting']);
        return;
      }
      this.loginError = await this.translate.loadFromBackend(loginErrorKey(error));
    }
  }

  onV2Failed() {
    this.recaptchaNeedsAttention = true;
  }

  //load translation from backend dynamically
  async handleAfterLogin() {
    const returnUrl = this.returnUrl;
    if (this.isExternalRedirectAllowed(returnUrl)) {
      window.location.href = returnUrl;
    } else {
      // navigateByUrl, never navigate([returnUrl]): the guard stores the URL with its query
      // string, and navigate() would encode `?`/`=` into the last path segment.
      await this.router.navigateByUrl(returnUrl);
    }
  }

  private isExternalRedirectAllowed(url: string): boolean {
    return isHardRedirectAllowed(url, window.location.origin);
  }

  ngOnInit(): void {
    const { noRegister, email } = this.route.snapshot.queryParams;
    if (email) {
      this.loginForm.patchValue({ email });
      this.loginForm.markAsDirty();
    }
    this.noRegister = !!noRegister;
    this.passkeySupported = this.passkeyService.supportsPasskeys();

    // One subscription, one login per Google sign-in — popup result or relayed token alike
    // (app#397; this used to subscribe to authState twice and log in twice).
    this.googleSignIn
      .signIns()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => void this.onGoogleSignIn(event));

    void this.startPasskeyAutofill();
  }

  ngOnDestroy(): void {
    this.passkeyService.cancelCeremony();
  }

  private async onGoogleSignIn(event: GoogleSignInEvent) {
    this.passkeyService.cancelCeremony();
    this.loginError = undefined;
    if ('error' in event) {
      // Cancelled at Google, a used/expired relay code, a state mismatch: one generic message,
      // nothing to retry with — the user simply clicks the button again.
      this.loginError = await this.translate.loadFromBackend('login_error_occurred_tr');
      return;
    }
    this.loginForm.disable();
    try {
      await this.authService.loginWithGoogle(event.idToken);
      await this.handleAfterLogin();
    } catch (error) {
      if (isAccountDisabledError(error)) {
        await this.router.navigate(['/waiting']);
        return;
      }
      this.loginForm.enable();
      this.loginError = await this.translate.loadFromBackend(loginErrorKey(error));
    }
  }

  private async startPasskeyAutofill() {
    if (!this.passkeyService.supportsPasskeys() || !(await this.passkeyService.supportsAutofill())) return;
    try {
      const response = await this.passkeyService.startConditionalLogin();
      await this.authService.loginWithPasskey(response);
      await this.handleAfterLogin();
    } catch {
      // The user never asked for this ceremony, so aborted/cancelled/failed ones stay silent —
      // the password form is untouched.
    }
  }

  /**
   * Explicit "Sign in with passkey" click: a modal ceremony that pops the OS picker immediately.
   * `startModalLogin()` mints a new abort signal, which cancels the pending conditional ceremony —
   * no extra `cancelCeremony()` call needed here.
   */
  async loginWithPasskey() {
    if (this.passkeyBusy) return;
    this.passkeyBusy = true;
    this.loginError = undefined;
    try {
      const response = await this.passkeyService.startModalLogin();
      await this.authService.loginWithPasskey(response);
      await this.handleAfterLogin();
    } catch (error: any) {
      // Unlike the autofill path this was a deliberate user action, so a genuine failure is shown.
      // A dismissed picker is not a failure — the user simply changed their mind.
      if (isAccountDisabledError(error)) {
        await this.router.navigate(['/waiting']);
      } else if (!isCancelledCeremony(error)) {
        this.loginError = await this.translate.loadFromBackend('passkey_login_failed_tr');
      }
    } finally {
      this.passkeyBusy = false;
    }
  }

  async loginRequest() {
    this.passkeyService.cancelCeremony();
    const form = this.loginForm.value;
    this.loginForm.disable();
    try {
      await this.authService.login(form.email!, form.password!);
      this.loginForm.markAsPristine();
    } catch (error: any) {
      console.log(error);
      if (isAccountDisabledError(error)) {
        await this.router.navigate(['/waiting']);
        return;
      }
      this.loginForm.enable();
    }
  }
}
